import {
  getLeoUuid,
  LeoUUID,
  getList,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TableFilter, TableFilterDTO, TableFilterEnums } from "../types/tableFilter";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLForSpocsWithFiltersRPC implements
  LeoRPC<GetPresignedDownloadURLForSpocsWithFiltersRPC.Request, GetPresignedDownloadURLForSpocsWithFiltersRPC.Response, GetPresignedDownloadURLForSpocsWithFiltersRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLForSpocsWithFiltersRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLForSpocsWithFiltersRPC.Response, GetPresignedDownloadURLForSpocsWithFiltersRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLForSpocsWithFiltersRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    filters: TableFilterDTO[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLForSpocsWithFiltersRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly filters: TableFilter[];
  
    static readonly constraints = {
      "filters": {
        "maxSize": 5,
      }
    }
  
    constructor(
    projectId: LeoUUID, 
    filters: TableFilter[]
    ) {
      this.projectId = projectId;
      Request.validateFilters(filters);
      this.filters = filters;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const filters = getList<TableFilterDTO, TableFilter>(
        dto,
        "filters",
        TableFilter.fromDTO,
        InvalidRequestFiltersError
      );
      return new Request(
        projectId,
        filters
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          filters: this.filters.map((e: TableFilter) => { return e.toDTO() })
      };
      return dto;
    }
    private static validateFilters = (filters: TableFilter[]): void => {
      if(filters.length > 5) {
        throw new InvalidRequestFiltersMaxSizeError(
          5,
          `Attribute filters is too long. Size is ${filters.length}. Max size is 5.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForSpocsWithFiltersRPC(projectId='${this.projectId}',filters='${this.filters}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLForSpocsWithFiltersResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForSpocsWithFilters(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForSpocsWithFiltersRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidProjectId;
    export type ErrorsDTO = InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDownloadURLForSpocsWithFiltersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestFiltersError extends GetPresignedDownloadURLForSpocsWithFiltersRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersError";
  }
}

export class InvalidRequestFiltersMaxSizeError extends InvalidRequestFiltersError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersMaxSizeError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDownloadURLForSpocsWithFiltersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDownloadURLForSpocsWithFiltersRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

